import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import cornerstone from 'cornerstone-core';
import './DicomViewer.css';

const TOUCH_PIXELS_PER_SLICE = 8;
// Pinch zoom limits, relative to the "fit to window" scale.
const MIN_ZOOM_FACTOR = 0.5;
const MAX_ZOOM_FACTOR = 12;
// Two quick taps within this window reset zoom/pan.
const DOUBLE_TAP_MS = 300;
const TAP_MOVE_TOLERANCE_PX = 10;
// Above this multiple of the fit scale, a one-finger drag pans instead of scrubbing.
const PAN_ZOOM_THRESHOLD = 1.02;
// Show the loading spinner only if the selected slice hasn't rendered by then.
const LOADING_SPINNER_DELAY_MS = 150;

const getTouchDistance = (touches) =>
  Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);

const getTouchMidpoint = (touches) => ({
  x: (touches[0].clientX + touches[1].clientX) / 2,
  y: (touches[0].clientY + touches[1].clientY) / 2
});

// TODO: REVISIT — in-viewer study/series switching was removed and the
// wiring around it is now half-connected.
//
// This component used to render a top bar with two <select> dropdowns
// letting the user jump between studies and between series without leaving
// the viewer. That bar, the `studyList` it iterated, the formatStudyLabel
// helper, and the `onStudyChange` / `onSeriesChange` props were all deleted.
// Only slice scrubbing within one series remains, so the user must close the
// viewer and click a different thumbnail to change series.
//
// The leftovers to clean up or re-wire:
//
//   - Both ImagesPage and UploadImages still pass onStudyChange and
//     onSeriesChange to this component. Nothing here destructures them, so
//     handleViewerStudyChange / handleViewerSeriesChange in both files are
//     unreachable code being kept alive by props that go nowhere.
//
//   - UploadImages.handleViewerStudyChange reads `firstSeries?.seriesKey`,
//     but series objects expose `seriesInstanceUID`. It resolves to
//     undefined and blanks the series key. That bug is dormant only because
//     the function can no longer be called — re-enable the dropdowns without
//     fixing it and study switching breaks immediately on that page.
//
//   - studyMap below still carries `studyDate` and a `series` array with
//     labels and slice counts. Nothing reads them now; they existed to
//     populate the dropdown option text.
//
// Decide one way or the other: either restore the navigation UI (and fix the
// seriesKey bug first), or delete the dead handlers and the unused props and
// trim studyMap down to what the viewer actually renders.
function DicomViewer({
  isOpen,
  onClose,
  studies,
  currentStudyUID,
  currentSeriesKey,
  currentIndex,
  onIndexChange
}) {
  const viewportRef = useRef(null);

  // Normalize the raw studies array into a lookup-friendly shape.
  // Only recomputes when the viewer is open and studies change.
  const studyMap = useMemo(() => {
    if (!isOpen || !studies?.length) {
      return {};
    }

    const map = {};

    for (const study of studies) {
      const series = [];
      const byKey = {};

      for (const seriesEntry of study.series || []) {
        const slices =
          Array.isArray(seriesEntry.slices) ? seriesEntry.slices
          : Array.isArray(seriesEntry.images) ? seriesEntry.images
          : Array.isArray(seriesEntry) ? seriesEntry
          : [];

        if (!slices.length) continue;

        const label =
          seriesEntry.label || seriesEntry.seriesDescription || seriesEntry.seriesInstanceUID;

        const seriesKey = seriesEntry.seriesKey || seriesEntry.seriesInstanceUID;
        series.push({ seriesKey, seriesInstanceUID: seriesEntry.seriesInstanceUID, label, slices });
        byKey[seriesKey] = slices;
      }

      map[study.studyInstanceUID] = {
        description: study.description || '',
        studyDate: study.studyDate || '',
        series,
        byKey
      };
    }

    return map;
  }, [isOpen, studies]);

  const currentStudy = currentStudyUID
    ? studyMap[currentStudyUID] || { description: '', series: [], byKey: {} }
    : { description: '', series: [], byKey: {} };

  const activeSeries = currentSeriesKey ? currentStudy.byKey[currentSeriesKey] || [] : [];
  const totalSlices = activeSeries.length;

  const latestRef = useRef({ currentIndex, totalSlices, onIndexChange });
  latestRef.current = { currentIndex, totalSlices, onIndexChange };

  // Mobile browsers often hand a range input's touch drag to scrolling instead
  // of moving the thumb. Drive the slider from pointer events for touch/pen and
  // leave native mouse behaviour alone.
  const sliderDragRef = useRef(false);

  const setIndexFromPointer = useCallback((e) => {
    const { totalSlices: total, onIndexChange: change, currentIndex: index } = latestRef.current;
    if (!total) return;
    const rect = e.currentTarget.getBoundingClientRect();
    if (!rect.width) return;
    const ratio = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
    const nextIndex = Math.round(ratio * (total - 1));
    if (nextIndex !== index) {
      change(nextIndex);
    }
  }, []);

  const handleSliderPointerDown = useCallback((e) => {
    if (e.pointerType === 'mouse') return;
    e.preventDefault();
    sliderDragRef.current = true;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (err) {
      // Pointer capture is best-effort.
    }
    setIndexFromPointer(e);
  }, [setIndexFromPointer]);

  const handleSliderPointerMove = useCallback((e) => {
    if (!sliderDragRef.current) return;
    e.preventDefault();
    setIndexFromPointer(e);
  }, [setIndexFromPointer]);

  const handleSliderPointerEnd = useCallback((e) => {
    if (!sliderDragRef.current) return;
    sliderDragRef.current = false;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (err) {
      // Already released.
    }
  }, []);

  // Enable cornerstone on the viewport element when the modal opens.
  useEffect(() => {
    const element = viewportRef.current;
    if (!element || !isOpen) return undefined;

    cornerstone.enable(element);
    return () => {
      try {
        cornerstone.disable(element);
      } catch (err) {
        // Ignore if already disabled
      }
    };
  }, [isOpen]);

  // True while the displayed slice lags noticeably behind the selected one.
  const [isLoadingSlice, setIsLoadingSlice] = useState(false);
  const renderSeqRef = useRef(0);

  // Render whenever the active slice changes (and the viewer is open).
  useEffect(() => {
    if (!isOpen || !viewportRef.current || !activeSeries.length) {
      setIsLoadingSlice(false);
      return undefined;
    }

    // Each change bumps the sequence; a load that finishes after a newer
    // request started is stale and must not be displayed.
    const seq = ++renderSeqRef.current;
    const element = viewportRef.current;
    let settled = false;

    // Only surface the spinner when the load is slow. Cached slices resolve
    // well inside this window, so smooth scrubbing never flashes it.
    const spinnerTimer = setTimeout(() => {
      if (!settled && renderSeqRef.current === seq) {
        setIsLoadingSlice(true);
      }
    }, LOADING_SPINNER_DELAY_MS);

    const renderImage = async () => {
      try {
        const image = await cornerstone.loadImage(activeSeries[currentIndex].imageId);
        if (renderSeqRef.current !== seq) return;
        cornerstone.displayImage(element, image);
      } catch (err) {
        // Swallow; parent can surface errors if needed.
      } finally {
        if (renderSeqRef.current === seq) {
          settled = true;
          clearTimeout(spinnerTimer);
          setIsLoadingSlice(false);
        }
      }
    };

    renderImage();

    return () => {
      settled = true;
      clearTimeout(spinnerTimer);
    };
  }, [activeSeries, currentIndex, isOpen]);

  // Re-fit the image when the window is resized.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleResize = () => {
      if (viewportRef.current) {
        try {
          cornerstone.resize(viewportRef.current, true);
        } catch (err) {
          // Ignore if no image displayed yet
        }
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen]);

  // Keyboard shortcuts: arrows to scrub, Esc to close.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        onIndexChange(Math.min(currentIndex + 1, Math.max(activeSeries.length - 1, 0)));
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        onIndexChange(Math.max(currentIndex - 1, 0));
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, activeSeries.length, currentIndex, onClose, onIndexChange]);

  // Lock body scroll while the modal is open.
  useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  useEffect(() => {
    const element = viewportRef.current;
    if (!element || !isOpen) return undefined;

    const clampIndex = (index) => {
      const { totalSlices: total } = latestRef.current;
      return Math.min(Math.max(index, 0), Math.max(total - 1, 0));
    };

    const handleWheel = (e) => {
      const { currentIndex: index, totalSlices: total, onIndexChange: change } = latestRef.current;
      if (!total) return;
      e.preventDefault();
      if (e.deltaY > 0) {
        change(clampIndex(index + 1));
      } else if (e.deltaY < 0) {
        change(clampIndex(index - 1));
      }
    };

    let touchStartY = null;
    let touchStartIndex = 0;
    // One-finger pan state while the image is zoomed in.
    let pan = null;
    // Pinch state while two fingers are down.
    let pinch = null;
    // Once a multi-touch gesture starts, ignore single-finger scrubbing until
    // every finger lifts, so releasing one finger doesn't jump slices.
    let gestureLocked = false;
    let tapCandidate = null;
    let lastTapTime = 0;

    const getFitScale = () => {
      try {
        const enabled = cornerstone.getEnabledElement(element);
        if (enabled?.image) {
          return cornerstone.getDefaultViewportForImage(element, enabled.image).scale;
        }
      } catch (err) {
        // Fall through to the current scale.
      }
      return null;
    };

    const resetView = () => {
      try {
        cornerstone.reset(element);
      } catch (err) {
        // Ignore if no image displayed yet
      }
    };

    const handleTouchStart = (e) => {
      if (e.touches.length === 2) {
        touchStartY = null;
        pan = null;
        tapCandidate = null;
        gestureLocked = true;
        try {
          const viewport = cornerstone.getViewport(element);
          if (!viewport) {
            pinch = null;
            return;
          }
          const fitScale = getFitScale() || viewport.scale;
          pinch = {
            startDistance: getTouchDistance(e.touches),
            startMidpoint: getTouchMidpoint(e.touches),
            startScale: viewport.scale,
            startTranslation: { x: viewport.translation.x, y: viewport.translation.y },
            minScale: fitScale * MIN_ZOOM_FACTOR,
            maxScale: fitScale * MAX_ZOOM_FACTOR
          };
        } catch (err) {
          pinch = null;
        }
        return;
      }

      if (e.touches.length !== 1 || gestureLocked) {
        touchStartY = null;
        tapCandidate = null;
        return;
      }
      tapCandidate = { x: e.touches[0].clientX, y: e.touches[0].clientY };

      // Zoomed in: drag moves the image. At fit size: drag scrubs slices.
      pan = null;
      try {
        const viewport = cornerstone.getViewport(element);
        const fitScale = getFitScale();
        if (viewport && fitScale && viewport.scale > fitScale * PAN_ZOOM_THRESHOLD) {
          pan = {
            startX: e.touches[0].clientX,
            startY: e.touches[0].clientY,
            startTranslation: { x: viewport.translation.x, y: viewport.translation.y }
          };
          touchStartY = null;
          return;
        }
      } catch (err) {
        // No image yet; fall back to scrubbing.
      }
      touchStartY = e.touches[0].clientY;
      touchStartIndex = latestRef.current.currentIndex;
    };

    const handleTouchMove = (e) => {
      if (e.touches.length === 2 && pinch) {
        e.preventDefault();
        if (!pinch.startDistance) return;

        const distance = getTouchDistance(e.touches);
        const midpoint = getTouchMidpoint(e.touches);
        const rect = element.getBoundingClientRect();
        const nextScale = Math.min(
          Math.max(pinch.startScale * (distance / pinch.startDistance), pinch.minScale),
          pinch.maxScale
        );

        // Keep the image point under the initial pinch midpoint fixed while
        // scaling, then pan by however far the midpoint has drifted.
        const px = pinch.startMidpoint.x - (rect.left + rect.width / 2);
        const py = pinch.startMidpoint.y - (rect.top + rect.height / 2);
        const translation = {
          x: pinch.startTranslation.x + px / nextScale - px / pinch.startScale
            + (midpoint.x - pinch.startMidpoint.x) / nextScale,
          y: pinch.startTranslation.y + py / nextScale - py / pinch.startScale
            + (midpoint.y - pinch.startMidpoint.y) / nextScale
        };

        try {
          const viewport = cornerstone.getViewport(element);
          cornerstone.setViewport(element, { ...viewport, scale: nextScale, translation });
        } catch (err) {
          // Ignore if no image displayed yet
        }
        return;
      }

      if (e.touches.length !== 1 || (touchStartY === null && !pan)) return;
      e.preventDefault();
      if (
        tapCandidate &&
        (Math.abs(e.touches[0].clientX - tapCandidate.x) > TAP_MOVE_TOLERANCE_PX ||
          Math.abs(e.touches[0].clientY - tapCandidate.y) > TAP_MOVE_TOLERANCE_PX)
      ) {
        tapCandidate = null;
      }

      if (pan) {
        try {
          const viewport = cornerstone.getViewport(element);
          if (!viewport) return;
          const translation = {
            x: pan.startTranslation.x + (e.touches[0].clientX - pan.startX) / viewport.scale,
            y: pan.startTranslation.y + (e.touches[0].clientY - pan.startY) / viewport.scale
          };
          cornerstone.setViewport(element, { ...viewport, translation });
        } catch (err) {
          // Ignore if no image displayed yet
        }
        return;
      }

      const { currentIndex: index, totalSlices: total, onIndexChange: change } = latestRef.current;
      if (!total) return;
      const deltaY = e.touches[0].clientY - touchStartY;
      const nextIndex = clampIndex(touchStartIndex + Math.round(deltaY / TOUCH_PIXELS_PER_SLICE));
      if (nextIndex !== index) {
        change(nextIndex);
      }
    };

    const handleTouchEnd = (e) => {
      touchStartY = null;
      pan = null;
      if (e.touches.length === 0) {
        pinch = null;
        gestureLocked = false;
        if (tapCandidate) {
          const now = Date.now();
          if (now - lastTapTime < DOUBLE_TAP_MS) {
            resetView();
            lastTapTime = 0;
          } else {
            lastTapTime = now;
          }
        }
        tapCandidate = null;
      } else if (e.touches.length < 2) {
        pinch = null;
      }
    };

    element.addEventListener('wheel', handleWheel, { passive: false });
    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: false });
    element.addEventListener('touchend', handleTouchEnd);
    element.addEventListener('touchcancel', handleTouchEnd);

    return () => {
      element.removeEventListener('wheel', handleWheel);
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
      element.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      className="scrollable"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.92)',
        zIndex: 999999,
        display: 'flex',
        flexDirection: 'column',
        color: '#e5e7eb'
      }}
    >
      <button
        type="button"
        onClick={onClose}
        style={{
          position: 'fixed',
          top: '0.75rem',
          right: '0.75rem',
          zIndex: 1000000,
          background: '#dc2626',
          color: '#fff',
          border: 'none',
          borderRadius: '6px',
          padding: '0.5rem 1rem',
          fontSize: '14px',
          fontWeight: 600,
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.5)'
        }}
        aria-label="Close viewer"
      >
        ✕ Close
      </button>

      <div className="dicom-viewer-stage">
        <div
          ref={viewportRef}
          style={{
            flex: 1,
            minHeight: 0,
            background: '#000',
            cursor: 'crosshair',
            touchAction: 'none'
          }}
        />
        {isLoadingSlice && (
          <div className="dicom-viewer-loading" role="status" aria-live="polite">
            <div className="dicom-viewer-spinner" />
            <span>Loading slice {currentIndex + 1}</span>
          </div>
        )}
      </div>

      <div
        style={{
          padding: '0.75rem 1rem',
          borderTop: '1px solid #1f2937',
          background: '#0b0b0b'
        }}
      >
        <div className="dicom-viewer-controls">
          <button
            type="button"
            disabled={currentIndex <= 0}
            onClick={() => onIndexChange(currentIndex - 1)}
          >
            ← Back
          </button>
          <input
            type="range"
            min="0"
            max={Math.max(totalSlices - 1, 0)}
            value={currentIndex}
            disabled={!totalSlices}
            onChange={(e) => onIndexChange(parseInt(e.target.value, 10))}
            onPointerDown={handleSliderPointerDown}
            onPointerMove={handleSliderPointerMove}
            onPointerUp={handleSliderPointerEnd}
            onPointerCancel={handleSliderPointerEnd}
            className="dicom-viewer-slider"
            aria-label="Slice"
          />
          <button
            type="button"
            className="dicom-viewer-next"
            disabled={!totalSlices || currentIndex >= totalSlices - 1}
            onClick={() => onIndexChange(currentIndex + 1)}
          >
            Next →
          </button>
          <span className="dicom-viewer-counter">
            {totalSlices ? currentIndex + 1 : 0} / {totalSlices}
          </span>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default DicomViewer;